import React, { useState, useRef, useEffect } from 'react';

// Work Order Status Options
export const WorkOrderStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED'
};

const STATUS_OPTIONS = [
  { 
    value: WorkOrderStatus.COMPLETED, 
    label: 'Completed',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    description: 'Work orders that have been completed'
  },
  { 
    value: WorkOrderStatus.OPEN, 
    label: 'Open',
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    description: 'Work orders that are open and waiting to be started'
  },
  { 
    value: WorkOrderStatus.IN_PROGRESS, 
    label: 'In Progress',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    description: 'Work orders currently being worked on'
  },
  { 
    value: WorkOrderStatus.CANCELED, 
    label: 'Canceled',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    description: 'Work orders that have been canceled'
  }
];

function WorkOrderStatusFilter({ selectedStatuses, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleStatus = (statusValue) => {
    if (selectedStatuses.includes(statusValue)) {
      // Remove status
      onChange(selectedStatuses.filter(s => s !== statusValue));
    } else {
      // Add status
      onChange([...selectedStatuses, statusValue]);
    }
  };

  const handleSelectAll = () => {
    onChange(STATUS_OPTIONS.map(s => s.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const isAllSelected = selectedStatuses.length === STATUS_OPTIONS.length;
  const isIndeterminate = selectedStatuses.length > 0 && selectedStatuses.length < STATUS_OPTIONS.length;

  const selectedStatusObjects = STATUS_OPTIONS.filter(status => 
    selectedStatuses.includes(status.value)
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Work Order Status
      </label>
      
      {/* Dropdown button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          <span className="flex items-center">
            {selectedStatusObjects.length === 0 ? (
              <span className="text-gray-500">Select statuses...</span>
            ) : selectedStatusObjects.length === STATUS_OPTIONS.length ? (
              <span>All statuses</span>
            ) : (
              <span className="flex items-center space-x-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedStatusObjects[0].bgColor} ${selectedStatusObjects[0].textColor}`}>
                  {selectedStatusObjects[0].label}
                </span>
                {selectedStatusObjects.length > 1 && (
                  <span className="text-gray-500">+{selectedStatusObjects.length - 1}</span>
                )}
              </span>
            )}
          </span>
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {/* Select all/Clear all buttons */}
            <div className="border-b border-gray-200 px-3 py-2 bg-gray-50">
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = isIndeterminate;
                      }
                    }}
                    onChange={() => {
                      if (isAllSelected) {
                        handleClearAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    {isAllSelected ? 'Deselect All' : 'Select All'}
                  </span>
                </label>
                {selectedStatuses.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {selectedStatuses.length} selected
                  </span>
                )}
              </div>
            </div>

            {/* Status options */}
            {STATUS_OPTIONS.map(status => (
              <div
                key={status.value}
                className={`cursor-pointer select-none py-3 pl-3 pr-9 hover:bg-gray-100 ${
                  selectedStatuses.includes(status.value) ? 'bg-purple-50' : ''
                }`}
                onClick={() => handleToggleStatus(status.value)}
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(status.value)}
                    onChange={() => {}} // Handled by div onClick
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{status.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected status badges */}
      {selectedStatusObjects.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedStatusObjects.map(status => (
            <span
              key={status.value}
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}
            >
              {status.label}
              <button
                type="button"
                onClick={() => handleToggleStatus(status.value)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <span className="sr-only">Remove {status.label}</span>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        {selectedStatuses.length === 0 
          ? 'Select at least one status to generate the report'
          : `Showing work orders with ${selectedStatuses.length} status${selectedStatuses.length !== 1 ? 'es' : ''}`
        }
      </p>
    </div>
  );
}

export default WorkOrderStatusFilter;

